import { useState, useEffect, useRef } from 'react';
import { MDBBtn } from 'mdb-react-ui-kit';

const slides = [
  {
    image: '/bandeau.jpg',
    bgPos: '20% center',
    label: null,
    title: 'Une Équipe · Deux Bateaux · Un Rêve',
    titleOnly: true,
    overlay: [0., 0.15],
    paddingTop: '15vh',
  },
  {
    image: '/bandeau_2.jpg',
    bgPos: 'center',
    label: "L'Équipe",
    title: 'Sébastien & Hugues',
    catchphrase: "Deux amis d'école d'ingénieurs, deux bateaux identiques, un seul objectif : se soutenir pour progresser ensemble.",
    overlay: [0.1, 0.85],
    align: 'right',
    paddingTop: '55vh',
  },
  {
    image: '/bandeau_old.jpg',
    bgPos: 'center 120%',
    label: 'Un nouvel objectif',
    title: "La Mini Transat'",
    catchphrase: ["La Rochelle · Les Canaries · Salvador de Bahia", "4 400 milles nautiques en course en solitaire"],
    overlay: [0.0, 0.15],
    paddingTop: '40vh',
  },
  {
    image: '/bandeau_4.jpg',
    bgPos: 'center',
    label: "Le Ruban Vert",
    title: "Naviguer pour sensibiliser au don d'organes",
    catchphrase: "22 500 patients attendent une greffe en France. Savoir pour ses proches, ça sauve des vies. Entre proches, on se le dit!",
    overlay: [0.0, 0.15],
    paddingTop: '10vh',
  },
  {
    image: '/bandeau_5.jpg',
    bgPos: 'center 120%',
    label: 'Les Bateaux',
    title: 'Le Mini 6.50',
    catchphrase: ["6m50 de long et 3m de large", "Léger, rapide, exigeant", "Pas de communication avec la terre"],
    overlay: [0.0, 0.1],
    align: 'right',
    paddingTop: '10vh',
  },
];

const newsletters = [
  { label: 'Janvier 2025', href: 'https://drive.google.com/file/d/1Nphj4HcU6dg7MbsdRllbHPfX9FAp4rI_/view?usp=sharing' },
  { label: 'Mai 2025',     href: 'https://drive.google.com/file/d/15XtWgxMYP37j0IADy9DNqgPeoltctuM8/view?usp=sharing' },
  { label: 'Août 2025',   href: 'https://drive.google.com/file/d/1cddLfYZ8ZUO1Sc3PHzFvbfOta7qQwdy6/view?usp=sharing' },
];

const COOLDOWN = 750; // ms entre deux changements de slide

export default function Hero() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const activeSlideRef    = useRef(0);
  const isTransitioning   = useRef(false);
  const touchStartY       = useRef(0);

  const goToSlide = (idx) => {
    activeSlideRef.current = idx;
    setActiveSlide(idx);
  };

  // Intercept wheel & touch while hero is in view
  useEffect(() => {
    const tryChange = (dir) => {
      // Ne pas intercepter si on a scrollé hors du hero
      if (window.scrollY > 10) return false;

      if (isTransitioning.current) return true; // avale l'événement pendant l'animation

      const cur = activeSlideRef.current;

      if (dir > 0 && cur < slides.length - 1) {
        isTransitioning.current = true;
        const next = cur + 1;
        activeSlideRef.current = next;
        setActiveSlide(next);
        setTimeout(() => { isTransitioning.current = false; }, COOLDOWN);
        return true;
      }

      if (dir < 0 && cur > 0) {
        isTransitioning.current = true;
        const prev = cur - 1;
        activeSlideRef.current = prev;
        setActiveSlide(prev);
        setTimeout(() => { isTransitioning.current = false; }, COOLDOWN);
        return true;
      }

      // Premier slide + scroll up  → rien (déjà en haut)
      // Dernier slide + scroll down → libère pour quitter le hero
      return false;
    };

    const onWheel = (e) => {
      if (tryChange(e.deltaY > 0 ? 1 : -1)) e.preventDefault();
    };

    const onTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 40) {
        if (tryChange(diff > 0 ? 1 : -1)) e.preventDefault();
      }
    };

    window.addEventListener('wheel',      onWheel,      { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true  });
    window.addEventListener('touchend',   onTouchEnd,   { passive: false });

    return () => {
      window.removeEventListener('wheel',      onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50%       { transform: translateX(-50%) translateY(8px); }
        }
        @media (max-width: 768px) {
          .hero-bg-1, .hero-bg-2, .hero-bg-3, .hero-bg-4 { background-position: 20% center !important; }
        }
      `}</style>

      {/* Hero — 100vh, simple */}
      <div style={{ height: '100vh', position: 'relative', overflow: 'hidden' }}>

        {/* Couches d'images + overlay par slide */}
        {slides.map((slide, i) => {
          const [ot, ob] = slide.overlay || [0.15, 0.35];
          return (
            <div key={i} style={{
              position: 'absolute', inset: 0,
              opacity: i === activeSlide ? 1 : 0,
              transition: 'opacity 0.8s ease',
              willChange: 'opacity',
            }}>
              <div className={`hero-bg-${i}`} style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${slide.image})`,
                backgroundSize: 'cover',
                backgroundPosition: slide.bgPos,
              }} />
              <div style={{
                position: 'absolute', inset: 0,
                background: `linear-gradient(to bottom, rgba(20,30,55,${ot}) 0%, rgba(20,30,55,${ob}) 100%)`,
              }} />
            </div>
          );
        })}

        {/* Contenus */}
        {slides.map((slide, i) => {
          const titleOnly = slide.titleOnly;
          const isRight = slide.align === 'right';
          const isLeft  = slide.align === 'left';
          return (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: titleOnly ? 'center' : isRight ? 'flex-end' : isLeft ? 'flex-start' : 'center',
            justifyContent: (titleOnly || slide.paddingTop) ? 'flex-start' : 'center',
            paddingTop: slide.paddingTop || (titleOnly ? '10vh' : '0px'),
            paddingBottom: titleOnly ? '0px' : slide.paddingTop ? '0px' : '80px',
            paddingLeft: isLeft ? '8%' : '24px',
            paddingRight: isRight ? '8%' : '24px',
            textAlign: titleOnly ? 'center' : (slide.align || 'center'),
            opacity: i === activeSlide ? 1 : 0,
            transform: i === activeSlide ? 'translateY(0)' : 'translateY(18px)',
            transition: 'opacity 0.65s ease 0.1s, transform 0.65s ease 0.1s',
            pointerEvents: i === activeSlide ? 'auto' : 'none',
          }}>
            {slide.label && (
              <span style={{
                color: '#4A9E47', fontFamily: 'Consolas, monospace',
                fontSize: '16px', letterSpacing: '4px',
                textTransform: 'uppercase', marginBottom: '14px', display: 'inline-block',
                fontWeight: 'bold',
                textShadow: '0 1px 8px rgba(0,0,0,0.9)',
              }}>
                {slide.label}
              </span>
            )}

            <h1 style={{
              color: '#fff', fontFamily: 'Consolas, monospace',
              fontSize: 'clamp(22px, 3.5vw, 48px)', fontWeight: 'bold',
              margin: '0 0 18px', lineHeight: 1.2,
              textShadow: '0 2px 12px rgba(0,0,0,0.7)',
            }}>
              {slide.title}
            </h1>

            {!slide.titleOnly && (
              <>
                <div style={{ width: '56px', height: '3px', backgroundColor: '#4A9E47', marginBottom: '20px' }} />
                <p style={{
                  color: 'rgba(255,255,255,0.88)', fontFamily: 'Consolas, monospace',
                  textShadow: '0 1px 6px rgba(0,0,0,0.7)',
                  fontSize: 'clamp(14px, 1.6vw, 19px)', maxWidth: '620px',
                  lineHeight: 1.65, margin: 0,
                }}>
                  {Array.isArray(slide.catchphrase)
                    ? slide.catchphrase.map((line, j) => <span key={j} style={{ display: 'block' }}>{line}</span>)
                    : slide.catchphrase}
                </p>
              </>
            )}
          </div>
          );
        })}

        {/* Dots de progression */}
        <div style={{
          position: 'absolute', right: '24px', top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 10,
        }}>
          {slides.map((_, i) => (
            <button key={i} onClick={() => goToSlide(i)}
              title={slides[i].label || slides[i].title}
              style={{
                width: '8px',
                height: i === activeSlide ? '28px' : '8px',
                borderRadius: '4px',
                backgroundColor: i === activeSlide ? '#4A9E47' : 'rgba(255,255,255,0.4)',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'height 0.35s ease, background-color 0.35s ease',
              }}
            />
          ))}
        </div>

        {/* Indicateur "défiler" sur le premier slide */}
        {activeSlide === 0 && (
          <div style={{
            position: 'absolute', bottom: '110px', left: '50%',
            animation: 'bounce 1.6s ease-in-out infinite',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
            zIndex: 10,
          }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Consolas', fontSize: '11px', letterSpacing: '2px' }}>
              DÉFILER
            </span>
            <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
              <path d="M1 1L8 8L15 1" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )}

        {/* Vague */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0, zIndex: 5, pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none"
            style={{ display: 'block', width: '100%', height: '80px' }}>
            <path fill="rgb(33, 46, 79)" d="M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </div>

      {/* Modal Newsletters */}
      {showNewsletterModal && (
        <div onClick={() => setShowNewsletterModal(false)} style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'linear-gradient(135deg, rgba(27,39,59,0.97) 0%, rgba(43,63,96,0.97) 100%)',
            border: '3px solid #fff', borderRadius: '18px',
            position: 'relative', padding: '40px 24px 24px',
            width: '90%', maxWidth: '360px',
            display: 'flex', flexDirection: 'column', gap: '16px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          }}>
            <button onClick={() => setShowNewsletterModal(false)} style={{
              position: 'absolute', top: '12px', right: '18px',
              background: 'transparent', border: 'none',
              color: '#4A9E47', fontSize: '24px', cursor: 'pointer', lineHeight: 1,
            }}>×</button>
            {newsletters.map(({ label, href }) => (
              <MDBBtn key={label} outline
                style={{ borderColor: '#4A9E47', color: '#fff', fontWeight: 'bold', fontFamily: 'Consolas, monospace' }}
                onClick={() => { window.open(href, '_blank', 'noopener,noreferrer'); setShowNewsletterModal(false); }}>
                {label}
              </MDBBtn>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
